import React from 'react';
import {Box, Button} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

const Bar = styled(Box)(({theme}) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    // Below MUI's Drawer (1200) / Dialog (1300) so the bottom sheets always
    // paint over the bar.
    zIndex: theme.zIndex.appBar,
    padding: theme.spacing(1.25, 2),
    paddingBottom: `calc(${theme.spacing(1.25)} + env(safe-area-inset-bottom, 0px))`,
    background: '#051726F2',
    backdropFilter: 'blur(8px)',
    borderTop: '1px solid #ffffff40',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
}));

const ShareButton = styled(Button)({
    borderRadius: '25px',
    height: '44px',
    padding: '0 28px',
    fontWeight: 700,
    fontSize: '1rem',
    textTransform: 'none',
    color: 'white',
    backgroundColor: '#00E162',
    flexShrink: 0,
    '&:hover': {backgroundColor: '#3D9A4A'},
});

interface StickyBarProps {
    onShare: () => void;
    onReset: () => void;
    /** Reposition mode: swap share/reset for the image-reposition approve/reset
     *  pair (the floating control bar is gone in Fill Mode). */
    reposition?: boolean;
    onRepositionApprove?: () => void;
    onRepositionReset?: () => void;
}

/** Fixed bottom bar: reset link + the primary share CTA. Progress is
 *  intentionally omitted — the per-field ✓ marks on the board and cards ARE
 *  the progress. While repositioning an image it hosts the reposition controls
 *  instead. */
const StickyBar: React.FC<StickyBarProps> = ({
                                                 onShare,
                                                 onReset,
                                                 reposition,
                                                 onRepositionApprove,
                                                 onRepositionReset,
                                             }) => {
    const {t} = useTranslation();

    if (reposition) {
        return (
            <Bar>
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    {/* Wrap so the click MouseEvent isn't forwarded as a positional
                        arg — the handler resets by input key and treats arg[0] as
                        that key, silently no-op'ing (scene) or throwing (legacy). */}
                    <Button variant="text" onClick={() => onRepositionReset?.()}
                            sx={{color: '#ffffff90', fontSize: '0.8rem', padding: '2px 0', minWidth: 0, textTransform: 'none'}}>
                        {t('editor.fillMode.repositionReset')}
                    </Button>
                </Box>
                <ShareButton onClick={() => onRepositionApprove?.()} data-testid="fill-reposition-done">
                    {t('editor.fillMode.repositionApprove')}
                </ShareButton>
            </Bar>
        );
    }

    return (
        <Bar>
            <Box sx={{flexGrow: 1, minWidth: 0}}>
                <Button variant="text" onClick={onReset}
                        sx={{color: '#ffffff90', fontSize: '0.8rem', padding: '2px 0', minWidth: 0, textTransform: 'none'}}>
                    {t('editor.buttons.resetTemplate')}
                </Button>
            </Box>
            <ShareButton onClick={onShare} data-testid="fill-share-cta">
                {t('editor.fillMode.shareCta')}
            </ShareButton>
        </Bar>
    );
};

export default StickyBar;
